import React, { useEffect, useState, useRef, useContext } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import ListingCalendarService from "../../../../../../../services/listing-calendar-service";
import { IdentityContext } from "../../../../../../../context/identity-context";
import "./block-date-modal.scss";

const BlockDateModal = ({
  show,
  onHide,
  selectedDateKey,
  onBlock,
  onBlockRollback,
  blockedItem,
  readOnly,
  onUnblock,
  onUnblockRollback,
}) => {
  const [reason, setReason] = useState("");
  const [isHoliday, setIsHoliday] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const [showUnblockConfirm, setShowUnblockConfirm] = useState(false);
  const [showBlockConfirm, setShowBlockConfirm] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [validationError, setValidationError] = useState(null);

  const serviceRef = useRef(null);
  if (serviceRef.current === null) serviceRef.current = new ListingCalendarService();
  const listingCalendarService = serviceRef.current;

  const { user } = useContext(IdentityContext);

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!show) {
      setReason("");
      setIsHoliday(false);
      setIsProcessing(false);
      setShowUnblockConfirm(false);
      setShowBlockConfirm(false);
      setErrorMessage("");
      setValidationError(null);
      return;
    }

    if (blockedItem) {
      setReason(blockedItem.notes || blockedItem.reason || "");
      setIsHoliday(
        typeof blockedItem.isHoliday === "boolean"
          ? blockedItem.isHoliday
          : !!blockedItem.holiday
      );
    } else {
      setReason("");
      setIsHoliday(false);
    }
  }, [show, blockedItem]);

  const prettyDate = (() => {
    if (!selectedDateKey) return "";
    try {
      const parts = selectedDateKey.split("-");
      if (parts.length !== 3) return selectedDateKey;
      const year = Number(parts[0]);
      const month = Number(parts[1]) - 1; // JS months 0-11
      const day = Number(parts[2]);
      const dt = new Date(year, month, day);
      if (isNaN(dt.getTime())) return selectedDateKey;
      return dt.toLocaleDateString(undefined, {
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return selectedDateKey;
    }
  })();

  const mainModalVisible = Boolean(show) && !showUnblockConfirm && !showBlockConfirm;

  async function findServerBlockedId(listingDateKey, notes) {
    try {
      const role = "listing officer"
      const res = await listingCalendarService.getBlockedDates(role);
      const listFromApi = (res && res.blockDates) ? res.blockDates : (res && res.blockedDates) ? res.blockedDates : (Array.isArray(res) ? res : []);
      if (!Array.isArray(listFromApi)) return null;

      for (const item of listFromApi) {
        const rawKey = item.listingDateKey || item.listingDate || item.date || null;
        if (!rawKey) continue;

        let norm = rawKey;
        try {
          const dt = new Date(rawKey);
          if (!isNaN(dt.getTime())) norm = dt.toISOString().slice(0, 10);
        } catch {}

        if (String(norm) === String(listingDateKey)) {
          if (notes) {
            const itemNotes = String(item.reason || item.notes || "").trim();
            if (itemNotes && itemNotes === String(notes).trim()) {
              return item.id || item._id || null;
            }
            return item.id || item._id || null;
          } else {
            return item.id || item._id || null;
          }
        }
      }
      return null;
    } catch (err) {
      console.warn("Failed to fetch blocked list while resolving server id:", err);
      return null;
    }
  }

  async function doBlock() {
    if (!selectedDateKey) return;

    const trimmed = reason && reason.trim();
    if (!trimmed) {
      // Safety double-check: should not reach here because UI prevents it, but keep it defensive
      if (isMountedRef.current) setValidationError("Please provide a reason for blocking.");
      return;
    }

    setIsProcessing(true);
    setErrorMessage("");
    let optimisticItem = null;

    try {
      const payload = {
        listingDateKey: selectedDateKey,
        isHoliday,
        reason: trimmed,
        admin: user?.attributes?.["custom:display_name"] || undefined,
      };

      if (typeof onBlock === "function") {
        try {
          optimisticItem = onBlock(selectedDateKey, { ...payload }) || null;
        } catch (optimisticErr) {
          console.warn("Optimistic onBlock threw:", optimisticErr);
        }
      }

      await listingCalendarService.adminLoBlockDates(payload);

      if (isMountedRef.current) {
        setShowBlockConfirm(false);
        onHide && onHide();
      }
    } catch (err) {
      console.error("Failed to block date:", err);
      try {
        if (typeof onBlockRollback === "function") {
          onBlockRollback(selectedDateKey, optimisticItem ? optimisticItem.id : null);
        }
      } catch (rbErr) {
        console.warn("onBlockRollback threw:", rbErr);
      }
      if (isMountedRef.current) setErrorMessage("Failed to block date. Try again.");
      setShowBlockConfirm(false);
    } finally {
      if (isMountedRef.current) setIsProcessing(false);
    }
  }

  function handleUnblockClick() {
    if (!blockedItem) {
      onHide && onHide();
      return;
    }

    if (blockedItem.isRegular) {
      setErrorMessage("This is a regular holiday and cannot be unblocked.");
      return;
    }

    setErrorMessage("");
    setShowUnblockConfirm(true);
  }

  async function doUnblock() {
    if (!blockedItem) {
      setShowUnblockConfirm(false);
      onHide && onHide();
      return;
    }

    if (blockedItem.isRegular) {
      setShowUnblockConfirm(false);
      setErrorMessage("This is a regular holiday and cannot be unblocked.");
      return;
    }

    setIsProcessing(true);
    setErrorMessage("");

    let previousBlockedItem = null;
    try {
      if (typeof onUnblock === "function") {
        try {
          previousBlockedItem = onUnblock(selectedDateKey, blockedItem.id);
        } catch (optimisticErr) {
          console.warn("Optimistic onUnblock threw:", optimisticErr);
        }
      }

      let serverId = blockedItem.id;
      const looksOptimistic = String(serverId || "").startsWith("BLOCKDATE#");

      if (looksOptimistic) {
        const found = await findServerBlockedId(blockedItem.listingDateKey || selectedDateKey, blockedItem.notes || blockedItem.reason);
        if (found) {
          serverId = found;
        } else {
          await new Promise((r) => setTimeout(r, 350));
          const found2 = await findServerBlockedId(blockedItem.listingDateKey || selectedDateKey, blockedItem.notes || blockedItem.reason);
          if (found2) serverId = found2;
        }
      }

      if (!serverId) {
        if (typeof onUnblockRollback === "function") {
          try { onUnblockRollback(previousBlockedItem); } catch(e){ console.warn(e); }
        }
        setErrorMessage("Couldn't confirm blocked date on server yet. Try again in a moment.");
        setShowUnblockConfirm(false);
        return;
      }

      await listingCalendarService.adminLoUnblockDate(serverId);

      if (isMountedRef.current) {
        setShowUnblockConfirm(false);
        onHide && onHide();
      }
    } catch (err) {
      console.error("Failed to unblock:", err);
      try {
        if (typeof onUnblockRollback === "function") {
          onUnblockRollback(previousBlockedItem);
        }
      } catch (rbErr) {
        console.warn("onUnblockRollback threw:", rbErr);
      }
      if (isMountedRef.current) setErrorMessage("Failed to unblock date. Try again.");
      setShowUnblockConfirm(false);
    } finally {
      if (isMountedRef.current) setIsProcessing(false);
    }
  }

  const onReasonChange = (e) => {
    const val = e.target.value;
    setReason(val);
    if (validationError && val.trim()) setValidationError(null);
    if (errorMessage) setErrorMessage("");
  };

  const attemptShowBlockConfirm = () => {
    setErrorMessage("");
    setValidationError(null);

    const trimmed = reason && reason.trim();
    if (!trimmed) {
      setValidationError("Please provide a reason for blocking.");
      return;
    }

    setShowBlockConfirm(true);
  };

  return (
    <>
      <Modal show={mainModalVisible} onHide={onHide} centered className="bdm-modal">
        <Form onSubmit={(e) => e.preventDefault()}>
          <Modal.Header closeButton>
            <Modal.Title>
              {blockedItem ? "Date Blocked" : prettyDate || selectedDateKey}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="bdm-modal-body">
            {errorMessage ? (
              <div className="mb-2 text-danger" role="alert">
                {errorMessage}
              </div>
            ) : null}

            <Form.Group className="mb-2">
              <Form.Label>Reason</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                className="bdm-textarea"
                value={reason}
                onChange={onReasonChange}
                maxLength={100}
                placeholder={blockedItem ? "" : "Type... (required)"}
                disabled={!!readOnly || isProcessing}
                required
                aria-required={true}
                isInvalid={!!validationError}
              />
              <Form.Control.Feedback type="invalid">{validationError}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group>
              <Form.Check
                type="checkbox"
                id="holiday-check"
                label="Holiday"
                checked={isHoliday}
                onChange={(e) => setIsHoliday(e.target.checked)}
                disabled={!!readOnly || isProcessing}
                required
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={onHide} disabled={isProcessing}>
              {readOnly ? "Close" : "Cancel"}
            </Button>

            {readOnly ? (
              <>
                {!blockedItem?.isRegular && (
                  <Button
                    variant="danger"
                    onClick={handleUnblockClick}
                    disabled={isProcessing}
                  >
                    {isProcessing ? "Unblocking..." : "Unblock"}
                  </Button>
                )}

                <Button variant="primary" onClick={onHide} disabled={isProcessing}>
                  OK
                </Button>
              </>
            ) : (
              <Button
                type="button"
                variant="primary"
                disabled={isProcessing}
                onClick={attemptShowBlockConfirm}
              >
                {isProcessing ? "Blocking..." : "Block"}
              </Button>
            )}
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal
        show={showUnblockConfirm}
        onHide={() => setShowUnblockConfirm(false)}
        centered
        className="bdm-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Unblock</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bdm-modal-body">
          Are you sure you want to unblock{" "}
          <strong>{prettyDate || selectedDateKey}</strong>?
          {/* {blockedItem && (blockedItem.notes || blockedItem.reason) ? (
            <div className="bdm-reason-box">
              <strong>Reason:</strong>
              <div className="bdm-reason-content">{blockedItem.notes || blockedItem.reason}</div>
            </div>
          ) : null} */}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowUnblockConfirm(false)}
            disabled={isProcessing}
          >
            Cancel
          </Button>
          <Button variant="danger" onClick={doUnblock} disabled={isProcessing}>
            {isProcessing ? "Unblocking..." : "Yes, unblock"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showBlockConfirm}
        onHide={() => setShowBlockConfirm(false)}
        centered
        className="bdm-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Block</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bdm-modal-body">
          Are you sure you want to block{" "}
          <strong>{prettyDate || selectedDateKey}</strong>?
          {reason ? (
            <div className="bdm-reason-box">
              <strong>Reason:</strong>
              <div className="bdm-reason-content">{reason}</div>
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowBlockConfirm(false)}
            disabled={isProcessing}
          >
            Cancel
          </Button>
          <Button variant="danger" onClick={doBlock} disabled={isProcessing}>
            {isProcessing ? "Blocking..." : "Yes, block"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default BlockDateModal;
